import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FileCheck } from 'lucide-react';
import type { PermitGasServiceChange, PermitSection, PermitSepticOrSewer } from '@officehub/shared';
import {
  MAX_PERMIT_NOTE_LENGTH,
  PERMIT_COUNT_FIELDS,
  PERMIT_GAS_SERVICE_CHANGES,
  PERMIT_SEPTIC_OR_SEWER,
  permitCountError,
} from './permitRules';
import { INPUT_CLASS, LABEL_CLASS, TEXTAREA_CLASS, TOGGLE_ACTIVE, TOGGLE_INACTIVE } from './styles';

export interface PermitFormProps {
  data: PermitSection;
  onUpdate: (d: Partial<PermitSection>) => void;
  /**
   * When a typed count or note is handed to `onUpdate`. 'blur' (default) suits a parent whose
   * onUpdate saves straight away (one save per box, not per keystroke). 'change' suits a parent that
   * only holds the value until a Save button, so a tap on Save can never miss a half-finished box.
   */
  commitOn?: 'blur' | 'change';
}

type YesNoField = 'sprinklersOrMonitoredAlarm' | 'treesRemoved' | 'wellOnParcel' | 'openPlanningPermitsOrHoa';
type CountField = (typeof PERMIT_COUNT_FIELDS)[number];
type DraftField = CountField | 'openPlanningPermitsOrHoaNote';

function omit<T extends object>(obj: T, key: keyof T): T {
  const { [key]: _dropped, ...rest } = obj;
  return rest as T;
}

const TOGGLE_BASE = 'flex-1 py-2.5 text-xs rounded-xl transition-all duration-150 min-h-[48px]';

const YES_NO_QUESTIONS: { field: YesNoField; label: string }[] = [
  { field: 'sprinklersOrMonitoredAlarm', label: 'Fire sprinklers or a monitored alarm?' },
  { field: 'treesRemoved', label: 'Trees removed for the unit or trench?' },
  { field: 'wellOnParcel', label: 'Well on the property?' },
  { field: 'openPlanningPermitsOrHoa', label: 'Open planning permit, permit conditions, or HOA approval?' },
];

const GAS_SERVICE_LABELS: Record<PermitGasServiceChange, string> = {
  none: 'No change',
  meter_upgrade: 'Meter upgrade',
  new_service: 'New service',
};

const SEPTIC_OR_SEWER_LABELS: Record<PermitSepticOrSewer, string> = {
  septic: 'Septic',
  sewer: 'Sewer',
};

/**
 * Site facts the county permit asks for that only the surveyor can see. Every
 * answer starts blank ("not asked yet"); a blank is never shown as "No".
 * Tapping the selected answer again clears it. A cleared field emits null so
 * survey_updateSection deletes it (it merges the fields it is given, #3230).
 *
 * Counts and the note are typed text, so they are held in a local draft and
 * committed when the box loses focus: the parent's data can lag a keystroke (the
 * main app only updates after the save round-trip), and one save per keystroke
 * would also lose the second digit of "12". Only an entry that is really empty
 * clears the stored value; text the browser cannot read as a number ("e", "-")
 * is an error and never clears anything.
 */
export function PermitForm({ data, onUpdate, commitOn = 'blur' }: PermitFormProps): React.ReactElement {
  const [drafts, setDrafts] = useState<Partial<Record<DraftField, string>>>({});
  // What the server would refuse is kept here with its reason and never emitted;
  // the stored value stays as it was until the entry is valid.
  const [badFields, setBadFields] = useState<Partial<Record<CountField, string>>>({});

  const forget = useCallback((field: DraftField) => {
    setDrafts((prev) => omit(prev, field));
    setBadFields((prev) => omit(prev, field as CountField));
  }, []);

  // A new stored value (another device, or our own save landing) replaces any
  // draft and error for that field. A draft therefore outlives its own commit until
  // the parent's data catches up, so the box never snaps back to the old value.
  useEffect(() => forget('smokeAlarmCount'), [data.smokeAlarmCount, forget]);
  useEffect(() => forget('coAlarmCount'), [data.coAlarmCount, forget]);
  useEffect(() => forget('openPlanningPermitsOrHoaNote'), [data.openPlanningPermitsOrHoaNote, forget]);

  // A null clears the stored field; the section types have no null, so the one
  // widening cast lives here (the other section forms emit null the same way).
  const emit = useCallback(
    (patch: Record<string, unknown>) => onUpdate(patch as Partial<PermitSection>),
    [onUpdate],
  );

  // Leaving the form (closing the section, changing step) with a box still focused fires no blur:
  // hand over whatever valid entry is still waiting, once, as the form goes away.
  const latest = useRef({ drafts, data, emit });
  latest.current = { drafts, data, emit };
  useEffect(
    () => () => {
      const { drafts: d, data: stored, emit: send } = latest.current;
      const patch: Record<string, unknown> = {};
      for (const field of PERMIT_COUNT_FIELDS) {
        const raw = d[field];
        if (raw === undefined) continue;
        const value = raw.trim() === '' ? null : Number(raw);
        if ((value === null || !permitCountError(field, value)) && value !== (stored[field] ?? null)) patch[field] = value;
      }
      const note = d.openPlanningPermitsOrHoaNote;
      if (note !== undefined && (note === '' ? null : note) !== (stored.openPlanningPermitsOrHoaNote ?? null)) {
        patch.openPlanningPermitsOrHoaNote = note === '' ? null : note;
      }
      if (Object.keys(patch).length > 0) send(patch);
    },
    [],
  );

  const choose = useCallback(
    <K extends keyof PermitSection>(field: K, value: PermitSection[K]) => {
      const next = data[field] === value ? null : value;
      const patch: Record<string, unknown> = { [field]: next };
      // The note explains a "yes"; once the answer is not yes it must not linger, hidden, in the
      // stored data (or come back, stale, if the answer flips to yes again).
      if (field === 'openPlanningPermitsOrHoa' && next !== true) {
        if (data.openPlanningPermitsOrHoaNote !== undefined) patch.openPlanningPermitsOrHoaNote = null;
        forget('openPlanningPermitsOrHoaNote');
      }
      emit(patch);
    },
    [data, emit, forget],
  );

  const commitCount = (field: CountField, rawIn?: string) => {
    const raw = rawIn ?? drafts[field];
    if (raw === undefined || (rawIn === undefined && badFields[field])) return;
    const value = raw.trim() === '' ? null : Number(raw);
    const why = value === null ? null : permitCountError(field, value);
    if (why) {
      setBadFields((prev) => ({ ...prev, [field]: why }));
      return;
    }
    if (value === (data[field] ?? null)) forget(field);
    else emit({ [field]: value });
  };

  const commitNote = (rawIn?: string) => {
    const raw = rawIn ?? drafts.openPlanningPermitsOrHoaNote;
    if (raw === undefined) return;
    const value = raw === '' ? null : raw;
    if (value === (data.openPlanningPermitsOrHoaNote ?? null)) forget('openPlanningPermitsOrHoaNote');
    else emit({ openPlanningPermitsOrHoaNote: value });
  };

  const typeInto = (field: DraftField, raw: string, unreadable = false) => {
    setDrafts((prev) => ({ ...prev, [field]: raw }));
    setBadFields((prev) =>
      unreadable
        ? { ...prev, [field]: permitCountError(field, Number.NaN) ?? 'Enter a whole number' }
        : omit(prev, field as CountField),
    );
    if (commitOn !== 'change' || unreadable) return;
    if (field === 'openPlanningPermitsOrHoaNote') commitNote(raw);
    else commitCount(field, raw);
  };

  const choiceButton = (testId: string, label: string, active: boolean, onClick: () => void) => (
    <button
      key={testId}
      type="button"
      data-testid={testId}
      aria-pressed={active}
      onClick={onClick}
      className={`${TOGGLE_BASE} ${active ? TOGGLE_ACTIVE : TOGGLE_INACTIVE}`}
    >
      {label}
    </button>
  );

  const countInput = (field: CountField, label: string) => (
    <div>
      <label className={LABEL_CLASS} htmlFor={`permit-${field}`}>
        {label}
      </label>
      <input
        id={`permit-${field}`}
        data-testid={`field-${field}`}
        type="number"
        inputMode="numeric"
        min={0}
        step={1}
        className={INPUT_CLASS}
        value={drafts[field] ?? data[field] ?? ''}
        onChange={(e) => typeInto(field, e.target.value, e.target.validity?.badInput === true)}
        onBlur={() => commitCount(field)}
        aria-invalid={badFields[field] ? true : undefined}
        aria-describedby={badFields[field] ? `permit-${field}-error` : undefined}
        placeholder="Count on site"
      />
      {badFields[field] && (
        <p
          id={`permit-${field}-error`}
          role="alert"
          data-testid={`field-${field}-error`}
          className="mt-1 text-xs font-semibold text-red-700"
        >
          {badFields[field]}
        </p>
      )}
    </div>
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-2 px-1">
        <FileCheck className="w-3.5 h-3.5 text-slate-400" />
        <h2 className="text-[11px] font-black text-slate-500 uppercase tracking-[0.1em]">Permit questions</h2>
        <div className="flex-1 h-px bg-gradient-to-r from-slate-200 to-transparent" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {countInput('smokeAlarmCount', 'Smoke alarms in the house')}
        {countInput('coAlarmCount', 'CO alarms in the house')}
      </div>

      {YES_NO_QUESTIONS.map(({ field, label }) => (
        <div key={field}>
          <div id={`permit-${field}-label`} className={LABEL_CLASS}>
            {label}
          </div>
          <div role="group" aria-labelledby={`permit-${field}-label`} className="flex gap-2">
            {choiceButton(`permit-${field}-yes`, 'Yes', data[field] === true, () => choose(field, true))}
            {choiceButton(`permit-${field}-no`, 'No', data[field] === false, () => choose(field, false))}
          </div>
        </div>
      ))}

      {data.openPlanningPermitsOrHoa === true && (
        <div>
          <label className={LABEL_CLASS} htmlFor="permit-openPlanningPermitsOrHoaNote">
            What needs approval?
          </label>
          <textarea
            id="permit-openPlanningPermitsOrHoaNote"
            data-testid="field-openPlanningPermitsOrHoaNote"
            className={TEXTAREA_CLASS}
            rows={2}
            maxLength={MAX_PERMIT_NOTE_LENGTH}
            value={drafts.openPlanningPermitsOrHoaNote ?? data.openPlanningPermitsOrHoaNote ?? ''}
            onChange={(e) => typeInto('openPlanningPermitsOrHoaNote', e.target.value)}
            onBlur={() => commitNote()}
            placeholder="Permit number, HOA name, conditions…"
          />
        </div>
      )}

      <div>
        <div id="permit-gasServiceChange-label" className={LABEL_CLASS}>
          Gas service change
        </div>
        <div role="group" aria-labelledby="permit-gasServiceChange-label" className="flex gap-2">
          {PERMIT_GAS_SERVICE_CHANGES.map((value) =>
            choiceButton(
              `permit-gasServiceChange-${value}`,
              GAS_SERVICE_LABELS[value],
              data.gasServiceChange === value,
              () => choose('gasServiceChange', value),
            ),
          )}
        </div>
      </div>

      <div>
        <div id="permit-septicOrSewer-label" className={LABEL_CLASS}>
          Septic or sewer
        </div>
        <div role="group" aria-labelledby="permit-septicOrSewer-label" className="flex gap-2">
          {PERMIT_SEPTIC_OR_SEWER.map((value) =>
            choiceButton(
              `permit-septicOrSewer-${value}`,
              SEPTIC_OR_SEWER_LABELS[value],
              data.septicOrSewer === value,
              () => choose('septicOrSewer', value),
            ),
          )}
        </div>
      </div>
    </div>
  );
}
